import { useContext } from 'react';
import { TeamMember } from '@marageti/z4-sdk/lib/people';
import { getMatchingUser } from '../../../utils/auth';
import { FeatureFlagContext, type FeatureName } from './feature-flag-context';

function emailAndRolesForAllowlists(teamMember: TeamMember | null): {
  email: string | undefined;
  roles: string[];
} {
  const email = teamMember?.email;
  if (!email) {
    return { email: undefined, roles: [] };
  }

  const matchedProfile = getMatchingUser(email, teamMember.userInfo ?? []);
  const roles = matchedProfile?.roles?.map(String) ?? [];

  return { email, roles };
}

export default function useFeatureFlag(featureName: FeatureName): boolean {
  const ctx = useContext(FeatureFlagContext);
  if (!ctx) {
    throw new Error('useFeatureFlag must be used within FeatureFlagProvider');
  }

  const { teamMember, features } = ctx;
  const { email, roles } = emailAndRolesForAllowlists(teamMember);

  const feature = features[featureName];
  if (!feature) {
    return false;
  }

  const { allowedRoles, allowedUsers } = feature;
  const hasRoleRule = (allowedRoles?.length ?? 0) > 0;
  const hasUsersRule = (allowedUsers?.length ?? 0) > 0;

  if (!hasRoleRule && !hasUsersRule) {
    return false;
  }

  const matchesRole = hasRoleRule && roles.some((role) => (allowedRoles ?? []).includes(role));
  const matchesUser = hasUsersRule && Boolean(email && (allowedUsers ?? []).includes(email));

  return matchesRole || matchesUser;
}
